import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, CheckCircle, Save } from 'lucide-react';

const COLORS = {
  navyBlue: '#003366', orange: '#FF6B35', mustard: '#FFAA00', green: '#16A34A',
  lightGray: '#F9FAFB', white: '#FFFFFF', borderGray: '#D1D5DB', textGray: '#6B7280', textDark: '#1F2937',
};

const fetchWithTimeout = (url, options = {}, timeoutMs = 30000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = window.sessionStorage.getItem('nwaAccessToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, signal: controller.signal }).finally(() => clearTimeout(timer));
};

const todayDDMMYYYY = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const parseDate = (value) => {
  if (!value) return null;
  const s = String(value).trim();
  let y; let m; let d;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) [y, m, d] = s.split('-').map(Number);
  else if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) [d, m, y] = s.split('/').map(Number);
  else if (/^\d{2}-\d{2}-\d{4}$/.test(s)) [d, m, y] = s.split('-').map(Number);
  else return null;
  const dt = new Date(y, m - 1, d);
  return (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) ? dt : null;
};

const completedAgeYears = (dobValue, screenValue) => {
  const dob = parseDate(dobValue); const screen = parseDate(screenValue);
  if (!dob || !screen || screen < dob) return null;
  let years = screen.getFullYear() - dob.getFullYear();
  if ((screen.getMonth() < dob.getMonth()) || (screen.getMonth() === dob.getMonth() && screen.getDate() < dob.getDate())) years -= 1;
  return years;
};

const formKeyForAge = (age) => {
  if (age == null || age < 8) return null;
  return age <= 16 ? '8-16' : 'post16';
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: COLORS.lightGray },
  header: { backgroundColor: COLORS.navyBlue, color: COLORS.white, padding: '8px 16px', borderBottom: `2px solid ${COLORS.mustard}` },
  headerContent: { maxWidth: '1100px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  backButton: { backgroundColor: 'transparent', border: 'none', color: COLORS.white, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', padding: '8px 0' },
  body: { maxWidth: '1100px', margin: '0 auto', padding: '24px' },
  card: { backgroundColor: COLORS.white, borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', padding: '24px', marginBottom: '20px' },
  label: { display: 'block', fontSize: '14px', fontWeight: '600', color: COLORS.textDark, marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: `2px solid ${COLORS.borderGray}`, borderRadius: '6px', fontSize: '15px', boxSizing: 'border-box' },
  select: { width: '100%', padding: '10px 14px', border: `2px solid ${COLORS.borderGray}`, borderRadius: '6px', fontSize: '15px', backgroundColor: COLORS.white, boxSizing: 'border-box' },
  button: { backgroundColor: COLORS.orange, color: COLORS.white, padding: '12px 24px', borderRadius: '6px', fontWeight: '600', border: 'none', cursor: 'pointer', fontSize: '16px' },
  errorBox: { backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' },
};

const interpretationStyle = (band) => {
  if (band === 'Below common average range') return { color: '#C2410C', bg: '#FFF7ED', border: '#FDBA74' };
  if (band === 'Above common average range') return { color: '#0369A1', bg: '#F0F9FF', border: '#7DD3FC' };
  return { color: '#15803D', bg: '#F0FDF4', border: '#86EFAC' };
};

export default function MALSScreening({ student, apiUrl, facilitatorId, facilitatorName, schoolId, schoolName, programmeId, programmeName, initialTimepoint, onBack, onSaved }) {
  const [config, setConfig] = useState(null);
  const [responses, setResponses] = useState({});
  const [screeningDate, setScreeningDate] = useState(todayDDMMYYYY());
  const [administrationMode, setAdministrationMode] = useState('Individual');
  const administrationBatchId = ''; // kept blank internally; no practitioner entry needed
  const [assessorName, setAssessorName] = useState(facilitatorName || '');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [stage, setStage] = useState('form');
  const timepoint = initialTimepoint || 'Baseline';

  useEffect(() => {
    let active = true;
    fetchWithTimeout(`${apiUrl}/api/mals/config`)
      .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
      .then(({ ok, data }) => {
        if (!active) return;
        if (!ok) throw new Error(data.error || 'Could not load Motivation configuration');
        setConfig(data);
      })
      .catch((e) => { if (active) setError(e.message || 'Could not load Motivation configuration'); });
    return () => { active = false; };
  }, [apiUrl]);

  const age = useMemo(() => completedAgeYears(student?.dob, screeningDate), [student?.dob, screeningDate]);
  const formKey = useMemo(() => formKeyForAge(age), [age]);
  const form = config?.forms?.[formKey] || null;
  const items = form?.items || [];

  useEffect(() => { setResponses({}); }, [formKey]);

  const answeredCount = items.filter((item) => responses[String(item.number)]).length;
  const missingCount = Math.max(0, items.length - answeredCount);
  const ready = !!form && items.length === 20 && missingCount === 0;

  const submit = async () => {
    if (!form) { setError('The supplied motivation forms are for learners aged 8 years and over.'); return; }
    if (!ready) { setError(`Please complete all 20 statements before saving (${missingCount} unanswered).`); return; }
    setError(''); setLoading(true);
    try {
      const res = await fetchWithTimeout(`${apiUrl}/api/mals/save`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_code: student.student_code, screening_date: screeningDate, timepoint, responses,
          age_years: age,
          school_id: schoolId || student?.school_id || '', school_name: schoolName || '',
          programme_id: programmeId || student?.programme_id || '', programme_name: programmeName || '',
          assessor_name: assessorName, administration_mode: administrationMode, administration_batch_id: administrationBatchId,
          entered_by_user_id: facilitatorId, entered_by_name: facilitatorName, notes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save this Motivation administration');
      setResult(data); setStage('complete'); if (onSaved) onSaved(data);
    } catch (e) { setError(e.message || 'Could not save this Motivation administration'); }
    finally { setLoading(false); }
  };

  const Header = ({ title }) => <div style={styles.header}><div style={styles.headerContent}>
    <button style={styles.backButton} onClick={onBack}><ArrowLeft size={18} /> Back to {student.full_name}</button>
    <span style={{ fontSize: '13px', color: COLORS.mustard, fontWeight: '600' }}>{title}</span>
  </div></div>;

  if (stage === 'complete') {
    const scoring = result?.scoring;
    const interp = scoring?.interpretation;
    const istyle = interpretationStyle(interp?.band);
    const diff = Number(interp?.difference_from_reference_mean || 0);
    return <div style={styles.container}><Header title="Motivation: Saved" /><div style={styles.body}><div style={styles.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: COLORS.green, marginBottom: '16px' }}><CheckCircle size={28} /><h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>Saved</h2></div>
      {scoring && <div style={{ border: `1px solid ${istyle.border}`, backgroundColor: istyle.bg, borderRadius: '8px', padding: '18px', margin: '16px 0' }}>
        <div style={{ fontSize: '13px', color: COLORS.textGray }}>{scoring.form_label} form</div>
        <div style={{ fontSize: '32px', fontWeight: '800', color: istyle.color, marginTop: '4px' }}>{scoring.total_score}/100</div>
        <div style={{ fontSize: '15px', fontWeight: '700', color: COLORS.textDark }}>{interp?.band}</div>
        <div style={{ fontSize: '13px', color: COLORS.textGray, marginTop: '6px' }}>Reference mean: 70 · Common average range: 60–80 · {diff === 0 ? 'At the reference mean' : `${Math.abs(diff)} points ${diff > 0 ? 'above' : 'below'} the reference mean`}</div>
      </div>}
      <button style={{ ...styles.button, backgroundColor: COLORS.white, color: COLORS.navyBlue, border: `2px solid ${COLORS.navyBlue}` }} onClick={onBack}>Back to {student.full_name}</button>
    </div></div></div>;
  }

  return <div style={styles.container}><Header title="Motivation" /><div style={styles.body}>
    <div style={styles.card}>
      <h1 style={{ fontSize: '22px', fontWeight: 'bold', color: COLORS.navyBlue, margin: '0 0 6px' }}>Motivation - {student.full_name}</h1>
      <p style={{ color: COLORS.textGray, margin: '0 0 14px' }}>Motivation profile - Timepoint: {timepoint}</p>
      <p style={{ fontSize: '14px', lineHeight: 1.55, color: COLORS.textDark, margin: '0 0 10px' }}>The learner responds to 20 statements about how they see themselves as a learner. There are no right or wrong answers.</p>
      <div style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.borderGray}`, borderRadius: '8px', padding: '12px 14px', fontSize: '13px', color: COLORS.textDark, lineHeight: 1.5, marginBottom: '18px' }}>
        <strong>Interpretation reference:</strong> about <strong>70</strong> is the usual mean and scores of <strong>60–80</strong> are described as commonly representing the average range. Use the score comparatively across time and alongside the learner's wider profile; age and local-group context can affect interpretation.
      </div>
      {error && <div style={styles.errorBox}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '14px' }}>
        <div><label style={styles.label}>Screening date (DD/MM/YYYY)</label><input style={styles.input} value={screeningDate} onChange={(e) => setScreeningDate(e.target.value)} /></div>
        <div><label style={styles.label}>Form selected by age</label><div style={{ ...styles.input, backgroundColor: '#F8FAFC', color: form ? COLORS.textDark : '#B91C1C', minHeight: '43px', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: form ? '600' : '400' }}>{form ? <><span>{form.label}</span><CheckCircle size={18} color={COLORS.green} /></> : 'No supplied motivation form for this age'}</div></div>
        <div><label style={styles.label}>Administration mode</label><select style={styles.select} value={administrationMode} onChange={(e) => setAdministrationMode(e.target.value)}><option value="Individual">Individual</option><option value="Group">Group</option></select></div>
        <div><label style={styles.label}>Assessor name</label><input style={styles.input} value={assessorName} onChange={(e) => setAssessorName(e.target.value)} /></div>
      </div>
    </div>

    {form && <div style={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '14px' }}>
        <div><h2 style={{ color: COLORS.navyBlue, fontSize: '18px', margin: 0 }}>How I see myself</h2><div style={{ fontSize: '12px', color: COLORS.textGray, marginTop: '3px' }}>{answeredCount}/20 statements completed</div></div>
        <div style={{ fontSize: '12px', color: COLORS.textGray }}>{form.label} <CheckCircle size={15} color={COLORS.green} style={{ verticalAlign: 'middle' }} /></div>
      </div>
      <div style={{ overflowX: 'auto' }}><div style={{ minWidth: '980px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(430px, 1fr) repeat(5, 100px)', gap: 0, padding: '0 8px 8px', color: COLORS.textGray, fontSize: '11px', fontWeight: '600' }}>
          <div style={{ textAlign: 'left' }}>Statement</div>{(config?.response_options || []).map((o) => <div key={o.code} style={{ textAlign: 'center', lineHeight: 1.2 }}>{o.code.toUpperCase()}</div>)}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(430px, 1fr) repeat(5, 100px)', padding: '0 8px 10px', fontSize: '10px', color: COLORS.textGray }}>
          <div />{(config?.response_options || []).map((o) => <div key={o.code} style={{ textAlign: 'center', padding: '0 5px', lineHeight: 1.2 }}>{o.label}</div>)}
        </div>
        {items.map((item) => <div key={item.number} style={{ display: 'grid', gridTemplateColumns: 'minmax(430px, 1fr) repeat(5, 100px)', alignItems: 'center', borderTop: '1px solid #E5E7EB', minHeight: '58px', padding: '0 8px' }}>
          <div style={{ fontSize: '14px', color: COLORS.textDark, padding: '9px 14px 9px 0', textAlign: 'left', lineHeight: 1.35 }}><strong style={{ color: COLORS.navyBlue, marginRight: '8px' }}>{item.number}.</strong>{item.text}</div>
          {(config?.response_options || []).map((option) => {
            const selected = responses[String(item.number)] === option.code;
            return <button key={option.code} type="button" aria-label={`${option.label}: ${item.text}`} aria-pressed={selected} onClick={() => setResponses((prev) => ({ ...prev, [String(item.number)]: option.code }))} style={{ justifySelf: 'center', width: '34px', height: '34px', borderRadius: '50%', border: selected ? `3px solid ${COLORS.navyBlue}` : '2px solid #9CA3AF', backgroundColor: selected ? '#E0F2FE' : COLORS.white, color: selected ? COLORS.navyBlue : COLORS.textGray, fontSize: '13px', fontWeight: '800', cursor: 'pointer', padding: 0 }}>{selected ? '✓' : option.code.toUpperCase()}</button>;
          })}
        </div>)}
      </div></div>
      <label style={{ ...styles.label, marginTop: '18px' }}>Recommendations / notes (optional)</label>
      <textarea style={{ ...styles.input, minHeight: '80px', fontFamily: 'inherit' }} value={notes} onChange={(e) => setNotes(e.target.value)} />
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginTop: '16px' }}>
        <button style={{ ...styles.button, display: 'flex', alignItems: 'center', gap: '8px', opacity: loading ? 0.65 : 1 }} onClick={submit} disabled={loading}><Save size={18} /> {loading ? 'Saving...' : 'Submit & Lock'}</button>
        <span style={{ fontSize: '13px', color: ready ? COLORS.green : COLORS.textGray, fontWeight: ready ? '600' : '400' }}>{ready ? 'All 20 statements complete - ready to save' : `${missingCount} statement${missingCount === 1 ? '' : 's'} still unanswered`}</span>
      </div>
    </div>}
  </div></div>;
}
